import { createContext, useContext, type ReactNode } from "react";

import { useMyOrganizations } from "./queries";

/**
 * The practice a staff member is maintaining in the admin panel
 * (`platform.clients.manage`). Inside a scope the doctor's own screens
 * (profile, brand kit, bookings, website) edit that practice through the
 * same endpoints; the server decides what staff may change and audits it.
 */
const ClientScopeContext = createContext<string | null>(null);

export function ClientScope({ orgId, children }: { orgId: string; children: ReactNode }) {
  return <ClientScopeContext.Provider value={orgId}>{children}</ClientScopeContext.Provider>;
}

/** The practice a screen works on, and whether staff are maintaining it. */
export interface CurrentOrg {
  orgId: string | undefined;
  /** True when a staff member edits a client's practice (no membership). */
  staff: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * The client in scope for staff, else the signed-in user's own practice.
 * Membership is not loaded inside a scope: staff are rarely members.
 */
export function useCurrentOrg(): CurrentOrg {
  const scoped = useContext(ClientScopeContext);
  const orgs = useMyOrganizations(scoped === null);
  if (scoped !== null) return { orgId: scoped, staff: true, isPending: false, isError: false, error: null };
  return {
    orgId: orgs.data?.[0]?.organization.id,
    staff: false,
    isPending: orgs.isPending,
    isError: orgs.isError,
    error: orgs.error,
  };
}
